import type { Node as ProsemirrorNode } from 'prosemirror-model';
import type { Decoration, EditorView } from 'prosemirror-view';
import type { FunctionComponent } from 'react';
import type { SecodaEditorComponentProps } from '../types';
import { NodeViewRenderer } from './NodeViewRenderer';

interface ComponentViewProps {
	component: FunctionComponent<SecodaEditorComponentProps>;
	editor: {
		renderers: Set<NodeViewRenderer<SecodaEditorComponentProps>>;
		componentViews: Set<ComponentView>;
		options: { readOnly?: boolean };
	};
	node: ProsemirrorNode;
	view: EditorView;
	getPos: () => number;
	decorations: Decoration[];
}

export default class ComponentView {
	component: FunctionComponent<SecodaEditorComponentProps>;

	editor: {
		renderers: Set<NodeViewRenderer<SecodaEditorComponentProps>>;
		componentViews: Set<ComponentView>;
		options: { readOnly?: boolean };
	};

	node: ProsemirrorNode;

	view: EditorView;

	getPos: () => number;

	decorations: Decoration[];

	renderer: NodeViewRenderer<SecodaEditorComponentProps>;

	isSelected = false;

	dom: HTMLElement | null;

	constructor({
		component,
		editor,
		node,
		view,
		getPos,
		decorations,
	}: ComponentViewProps) {
		this.component = component;
		this.editor = editor;
		this.getPos = getPos;
		this.decorations = decorations;
		this.node = node;
		this.view = view;
		this.dom = node.type.spec.inline
			? document.createElement('span')
			: document.createElement('div');

		this.dom.classList.add(`component-${node.type.name}`);
		this.renderer = new NodeViewRenderer(this.dom, this.component, this.props);

		// Register this ComponentView with the editor so it can be force-updated
		// when readOnly state changes (see setReadOnly in SecodaEditor)
		this.editor.renderers.add(this.renderer);
		this.editor.componentViews.add(this);
	}

	update(node: ProsemirrorNode) {
		if (node.type !== this.node.type) {
			return false;
		}

		this.node = node;
		this.renderer.updateProps(this.props);
		return true;
	}

	selectNode() {
		if (this.isEditable) {
			this.isSelected = true;
			this.renderer.updateProps(this.props);
		}
	}

	deselectNode() {
		if (this.isEditable) {
			this.isSelected = false;
			this.renderer.updateProps(this.props);
		}
	}

	stopEvent() {
		return true;
	}

	destroy() {
		// Unregister this ComponentView when it's destroyed to prevent memory leaks
		// and ensure setReadOnly doesn't try to update destroyed views
		this.editor.renderers.delete(this.renderer);
		this.editor.componentViews.delete(this);
		this.dom = null;
	}

	ignoreMutation() {
		return true;
	}

	/**
	 * Get the editable state of the editor. ProseMirror's EditorView.editable
	 * can be either a function or a boolean, so we handle both cases consistently.
	 */
	get isEditable() {
		const { editable } = this.view;
		if (typeof editable === 'function') {
			return (editable as () => boolean)();
		}
		// Fallback to editor.options.readOnly when editable is not a function
		// (e.g., during initialization or in edge cases)
		return !this.editor.options.readOnly;
	}

	get props() {
		return {
			node: this.node,
			view: this.view,
			isSelected: this.isSelected,
			isEditable: this.isEditable,
			getPos: this.getPos,
		} as SecodaEditorComponentProps;
	}

	/**
	 * Force update the React component props. This is called by setReadOnly
	 * to ensure all ComponentViews reflect the new readOnly state immediately.
	 */
	forceUpdate() {
		this.renderer.updateProps(this.props);
	}
}
